/**
 * DocsLink — the way out of the panel and into the customer-facing API
 * reference.
 *
 * The reference is a static site served from the same origin (`/docs/api/` by
 * Caddy, `/docs` in development — see `vite.config.js`), but it is *not* a route
 * of this single-page app. That is why this is a plain anchor and not a
 * `react-router` `Link`: a router link would try to render a screen that does
 * not exist and the customer would land on the panel's own 404 while the real
 * page sat one URL away.
 *
 * It opens in a new tab because the reference is something you read *while*
 * working in the panel (copy a key here, check the header name there), and
 * because losing the project you were looking at to read one curl example is a
 * worse trade. `rel="noreferrer"` keeps the panel URL out of the docs request.
 *
 * `VITE_DOCS_URL` overrides the target for a deployment that publishes the
 * reference somewhere else (a separate docs host, a sub-path). The default is
 * where the shipped Caddyfile serves it.
 */

const DEFAULT_DOCS_URL = "/docs/api/";

/** docsUrl is the absolute or root-relative URL of the API reference. */
export function docsUrl() {
  const configured =
    typeof import.meta !== "undefined" && import.meta.env ? import.meta.env.VITE_DOCS_URL : "";
  const url = (configured || "").trim() || DEFAULT_DOCS_URL;
  return url;
}

export default function DocsLink({ children = "API reference", style, ...rest }) {
  return (
    <a
      href={docsUrl()}
      target="_blank"
      rel="noreferrer"
      // The arrow is not decoration: it is the only cue that this leaves the app.
      title="Opens the API reference in a new tab"
      style={{ color: "var(--text-accent)", textDecoration: "none", fontSize: "var(--text-sm)", ...style }}
      {...rest}
    >
      {children}
      <span aria-hidden="true"> ↗</span>
    </a>
  );
}
